import {safeLocalStorage, safeSessionStorage} from "@/lib/utils/storage";

/** localStorage key holding the accumulated, normalized SMS campaign tags (JSON string[]). */
export const SMS_TAGS_STORAGE_KEY = "smsTags";
/** sessionStorage key prefix marking a tag whose click was already reported this browser session. */
export const SMS_TAG_CLICKED_SESSION_PREFIX = "smsTagClicked:";

/** Raw `?tag=` value accepted from the URL — case-sensitive (traffic-hash-style tags). */
const RAW_TAG_PATTERN = /^[a-zA-Z0-9_-]{1,64}$/;

/** Backend caps the attach payload at 20 tags. */
export const MAX_SMS_TAGS = 20;

/** Trim only — tag names are case-sensitive; returns null when validation fails. */
export function normalizeSmsTag(raw: string | null | undefined): string | null {
    if (!raw) return null;
    const trimmed = raw.trim();
    if (!RAW_TAG_PATTERN.test(trimmed)) return null;
    return trimmed;
}

/** Reads the accumulated tags from localStorage (defensive against malformed JSON). */
export function readSmsTags(): string[] {
    const raw = safeLocalStorage.getItem(SMS_TAGS_STORAGE_KEY);
    if (!raw) return [];
    try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed)
            ? parsed.filter((t): t is string => typeof t === "string")
            : [];
    } catch {
        return [];
    }
}

/** Adds a normalized tag to the localStorage set (dedup + accumulation across sessions). */
export function persistSmsTag(tag: string): void {
    const tags = readSmsTags();
    if (tags.includes(tag)) return;
    tags.push(tag);
    safeLocalStorage.setItem(SMS_TAGS_STORAGE_KEY, JSON.stringify(tags));
}

/** Clears the accumulated tags — called after a successful attach. */
export function clearSmsTags(): void {
    safeLocalStorage.removeItem(SMS_TAGS_STORAGE_KEY);
}

/**
 * Fire-and-forget public click report — one per tag per browser session.
 * No auth, no credentials; every error is swallowed so it can never block render.
 */
export function reportSmsTagClick(tag: string): void {
    const sessionKey = `${SMS_TAG_CLICKED_SESSION_PREFIX}${tag}`;
    if (safeSessionStorage.getItem(sessionKey)) return;
    // Mark before the request resolves: dedup is per browser session, not per success.
    safeSessionStorage.setItem(sessionKey, "1");

    const baseUrl = process.env.NEXT_PUBLIC_WALLET_API_URL;
    if (!baseUrl) return;
    try {
        void fetch(`${baseUrl}/api/v1/tags/${encodeURIComponent(tag)}/click`, {
            method: "POST",
            credentials: "omit",
            keepalive: true,
        }).catch(() => {});
    } catch {
        // never surface — click tracking is best-effort
    }
}

/**
 * Captures a raw `?tag=` value: validate → normalize → persist → report the click.
 * Returns the normalized tag (or null when invalid). Safe to call from an effect.
 */
export function captureSmsTag(raw: string | null | undefined): string | null {
    const tag = normalizeSmsTag(raw);
    if (!tag) return null;
    persistSmsTag(tag);
    reportSmsTagClick(tag);
    return tag;
}
